"use client";

import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ResponsiveModalContent } from "@/components/ui/responsive-modal";
import { planOf } from "@/lib/plans";
import { getProfile } from "@/lib/relationships.functions";
import { requestUpgrade, myUpgradeRequest } from "@/lib/upgrade.functions";
import { setUpgradeOpen, useUpgradeSheet, type UpgradeSource } from "@/lib/upgrade-store";
import { track } from "@/lib/track";

/** What the user just tapped, said back to them in one line. */
const CONTEXT: Record<UpgradeSource, string> = {
  "live-context": "Live Context reads your calendar and inbox — prep before every meeting, hooks after it.",
  "locked-moments": "Pyng found more moments today than your plan can open.",
  "deep-watch": "Deep Watch re-reads your most important people far more often.",
  boosts: "Boosts re-scan a person on demand, right when you need them.",
  people: "Your network is at its limit on Free.",
  topics: "Interest and topic monitoring runs standing searches beyond your people.",
  "daily-touches": "Free surfaces one reason to reach out a day.",
  settings: "Pro opens up the rest of Pyng.",
  generic: "Pro opens up the rest of Pyng.",
};

/** One "what changes" line: the limit today, then the limit on Pro. */
function Row({ label, from, to }: { label: string; from: string; to: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 text-[14px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="shrink-0 tabular-nums">
        {from ? <span className="text-muted-foreground/70">{from} &rarr; </span> : null}
        <span className="font-medium text-foreground">{to}</span>
      </span>
    </div>
  );
}

function num(n: number) {
  return n >= 100000 ? "∞" : String(n);
}

export function UpgradeSheet() {
  const { open, source, note } = useUpgradeSheet();
  const fetchProfile = useServerFn(getProfile);
  const fetchMine = useServerFn(myUpgradeRequest);
  const ask = useServerFn(requestUpgrade);
  const [done, setDone] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => fetchProfile(),
    enabled: open,
  });
  const { data: mine } = useQuery({
    queryKey: ["my-upgrade-request"],
    queryFn: () => fetchMine(),
    enabled: open,
  });

  const free = planOf("free");
  const pro = planOf("pro");
  const current = planOf((profile as any)?.plan);
  const requested = done || !!(mine as any)?.request;

  // One event per open, tagged with the plan the person is actually on.
  const logged = useRef(false);
  useEffect(() => {
    if (!open) {
      logged.current = false;
      return;
    }
    if (logged.current) return;
    logged.current = true;
    track("upgrade_sheet_open", { source, plan: current.id });
  }, [open, source, current.id]);

  const submit = useMutation({
    mutationFn: () => ask({ data: { plan: "pro" as const, source, note } }),
    onSuccess: () => {
      setDone(true);
      track("upgrade_request_submitted", { source, plan: current.id, requestedPlan: "pro" });
      toast.success("You're on the list — we'll turn Pro on for you shortly.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't send that"),
  });

  return (
    <Sheet open={open} onOpenChange={setUpgradeOpen}>
      <ResponsiveModalContent sheetClassName="rounded-t-3xl max-h-[92vh] overflow-y-auto no-scrollbar">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--accent-neon-ink)]" strokeWidth={1.75} />
            Upgrade to Pro
          </SheetTitle>
          <SheetDescription>{CONTEXT[source] ?? CONTEXT.generic}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-5 px-4 pb-6 sm:px-0">
          <p className="text-[15px]">
            <span className="font-semibold">Pro</span>
            <span className="text-muted-foreground"> &middot; {pro.priceMonthly} {pro.unit}</span>
          </p>

          {/* Only the handful of numbers that actually move. The full grid
              lives on /pricing — repeating it here just made the sheet loud. */}
          <div className="divide-y divide-foreground/[0.06] border-y border-foreground/[0.06]">
            <Row label="People you're watching" from={num(free.maxPeople)} to={num(pro.maxPeople)} />
            <Row label="Reach-outs a day" from={num(free.maxDailyTouches)} to={num(pro.maxDailyTouches)} />
            <Row label="Deep Watch" from={num(free.maxDeepWatch)} to={num(pro.maxDeepWatch)} />
            <Row label="Live Context" from="" to="Calendar and inbox" />
          </div>

          {current.id !== "free" ? (
            <p className="rounded-2xl glass-soft px-4 py-3 text-[13px] text-muted-foreground">
              You're already on {current.name}.
            </p>
          ) : requested ? (
            <div className="flex items-center gap-2 rounded-2xl glass-soft px-4 py-3 text-[13px] text-emerald-400">
              <Check className="h-4 w-4 shrink-0" />
              You're on the list — we'll turn Pro on for you shortly.
            </div>
          ) : (
            <button
              onClick={() => !submit.isPending && submit.mutate()}
              disabled={submit.isPending}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary text-[15px] font-medium text-primary-foreground disabled:opacity-60"
            >
              {submit.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {submit.isPending ? "Sending…" : "Request Pro"}
            </button>
          )}

          <p className="text-center text-[12px] text-muted-foreground">
            Pyng is invite-only while we're in beta, so Pro is switched on by hand — no card needed yet.
          </p>

          <div className="text-center text-[12px]">
            <Link
              to="/pricing"
              onClick={() => {
                track("upgrade_pricing_link_click", { source, plan: current.id });
                setUpgradeOpen(false);
              }}
              className="text-primary underline-offset-2 hover:underline"
            >
              See full plan comparison
            </Link>
          </div>
        </div>
      </ResponsiveModalContent>
    </Sheet>
  );
}
